import type { ChatClient, ChatRoomSummary } from '@wtfalch/chat';
import { useEffect, useState } from 'react';

import { ColleagueHours, type ColleagueHoursSource } from '../hours/ColleagueHours.tsx';
import { usePeople } from '../ui/people.tsx';
import { shortName } from './format.ts';
import { freeUntilLabel } from './freeUntil.ts';
import { AvatarWithPresence, StatusLabel } from './presence.tsx';

/** How far ahead "free until" looks: the rest of today is all it can say. */
const LOOKAHEAD_MS = 24 * 3_600_000;
const FREE_SLOT = 'PT30M';

/** The mail address a chat id stands for on the company domain, or `null`. */
function mailFor(chatUserId: string, domain: string | null | undefined): string | null {
  const local = /^@([^:]+):/.exec(chatUserId)?.[1];
  return local && domain ? `${local}@${domain}` : null;
}

/** "Free until 3:30 PM" for the person, read from their shared calendar.
 *  `null` while it loads, when their calendar is not shared with us, when
 *  they are busy now, or when there is no calendar to ask. */
function useFreeUntil(chatUserId: string): string | null {
  const people = usePeople();
  const calendar = people?.calendar ?? null;
  const email = mailFor(chatUserId, people?.mailDomain);
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    setLabel(null);
    if (!calendar || !email) return;
    let live = true;
    (async () => {
      const principal = await calendar.resolvePrincipal(email);
      if (!principal) return;
      const now = new Date();
      const found = await calendar.findTime(
        [principal.accountId],
        {
          after: now.toISOString(),
          before: new Date(now.getTime() + LOOKAHEAD_MS).toISOString(),
        },
        FREE_SLOT,
        { workingHours: true },
      );
      if (live && found.unknown.length === 0) setLabel(freeUntilLabel(found.slots, now));
    })().catch(() => {
      if (live) setLabel(null);
    });
    return () => {
      live = false;
    };
  }, [calendar, email]);
  return label;
}

/**
 * The top of a direct message's info panel: who the other side is, whether
 * they are around, and until when they are free. Their role is not here:
 * Agora reads no directory of roles or teams (`ui/people.tsx`).
 */
export function PersonHeader({
  client,
  room,
  userId,
  hours,
}: {
  client: ChatClient;
  room: ChatRoomSummary;
  userId: string;
  hours?: ColleagueHoursSource | undefined;
}) {
  const free = useFreeUntil(userId);
  const name = shortName(room.name);
  return (
    <div className="flex flex-col items-center gap-2 py-4 px-4 chat-person-header">
      <AvatarWithPresence client={client} userId={userId} name={name} seed={room.id} />
      <span className="h3 truncate max-w-full">{name}</span>
      <StatusLabel client={client} userId={userId} />
      {free && <span className="text-xs text-muted chat-person-free">{free}</span>}
      {hours && <ColleagueHours source={hours} chatUserId={userId} />}
    </div>
  );
}
